import { powtoonBE } from "@/lib/powtoon-be/client";
import { powtoonS2S } from "@/lib/powtoon-s2s/client";
import type {
  ScoreBreakdownEntry,
  ScoreBreakdownItem,
  ScoreResult,
  ScoringJobResponse,
} from "@/lib/powtoon-be/types";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("scoring-job.client");

// TODO: remove once Powtoon BE scoring fix is deployed.
// Temporary bypass so SCORM end-to-end testing can proceed while the
// backend rejects the new questionnaire payload shape.
const FAKE_SCORE_ENABLED = false;

export async function startScoringJob(input: {
  scenarioId: string;
  transcriptObjectId: string;
  publicLinkToken?: string;
  provider?: string;
  questionnaireIds?: string[];
  interactionMode?: string;
  /**
   * Id of the role-play scenario that was played. The BE's `select_scenario()`
   * matches `scenario_id` against the role-play object's `scenarios[]`, so for
   * role play this id — not the project id — goes into `scenario_id`.
   */
  rolePlayScenarioId?: string;
}): Promise<{ transcriptObjectId: string }> {
  if (FAKE_SCORE_ENABLED) {
    return { transcriptObjectId: input.transcriptObjectId };
  }
  const scoringScenarioId =
    input.interactionMode === "role_play"
      ? input.rolePlayScenarioId
      : input.scenarioId;
  if (input.publicLinkToken) {
    logger.info("scoring job start requested", {
      scenario_id: input.scenarioId,
      scoring_scenario_id: scoringScenarioId ?? null,
      transcript_object_id: input.transcriptObjectId,
      provider: input.provider,
      questionnaire_id: input.questionnaireIds?.[0] ?? null,
      public: true,
    });
    const res = await powtoonS2S.startPublicScoringJob({
      scenarioId: scoringScenarioId,
      transcriptObjectId: input.transcriptObjectId,
      publicLinkToken: input.publicLinkToken,
      provider: input.provider,
      questionnaireIds: input.questionnaireIds,
      interactionMode: input.interactionMode,
    });
    logger.info("scoring job start accepted", {
      scenario_id: input.scenarioId,
      transcript_object_id: input.transcriptObjectId,
      job_id: res.id ?? null,
      status: res.status ?? null,
      public: true,
    });
    return { transcriptObjectId: res.id ?? input.transcriptObjectId };
  }
  logger.info("scoring job start requested", {
    scenario_id: input.scenarioId,
    scoring_scenario_id: scoringScenarioId ?? null,
    transcript_object_id: input.transcriptObjectId,
    provider: input.provider,
    questionnaire_id: input.questionnaireIds?.[0] ?? null,
    public: false,
  });
  const res = await powtoonBE.scoringJobs.start(input.transcriptObjectId, {
    scenarioId: scoringScenarioId,
    provider: input.provider,
    questionnaireIds: input.questionnaireIds,
    interactionMode: input.interactionMode,
  });
  logger.info("scoring job start accepted", {
    scenario_id: input.scenarioId,
    transcript_object_id: input.transcriptObjectId,
    job_id: res.id ?? null,
    status: res.status ?? null,
    public: false,
  });
  return { transcriptObjectId: res.id ?? input.transcriptObjectId };
}

async function getScoringJob(input: {
  scenarioId: string;
  transcriptObjectId: string;
  publicLinkToken?: string;
}): Promise<ScoringJobResponse> {
  if (input.publicLinkToken) {
    return powtoonBE.scoringJobs.getPublic(
      input.transcriptObjectId,
      input.publicLinkToken,
    );
  }
  return powtoonBE.scoringJobs.get(input.transcriptObjectId);
}

function normalizeBreakdown(
  raw: Record<string, ScoreBreakdownItem> | undefined,
): ScoreBreakdownEntry[] {
  if (!raw || typeof raw !== "object") return [];
  const entries: ScoreBreakdownEntry[] = [];
  for (const [question_id, item] of Object.entries(raw)) {
    if (!item || typeof item !== "object") continue;
    const score = String(item.score ?? "");
    const comment = String(item.comment ?? "");
    if (!score && !comment) continue;
    entries.push({ question_id, score, comment });
  }
  return entries;
}

export async function pollScoringJob(input: {
  scenarioId: string;
  transcriptObjectId: string;
  publicLinkToken?: string;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
}): Promise<ScoreResult> {
  if (FAKE_SCORE_ENABLED) {
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, 1500);
      input.signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(t);
          reject(new Error("aborted"));
        },
        { once: true },
      );
    });
    return {
      pass: true,
      grade: 87,
      breakdown: [
        {
          question_id: "q_fake_1",
          score: "pass",
          comment: "Fake: scoring backend bypassed for SCORM testing.",
        },
      ],
    };
  }

  const interval = input.intervalMs ?? 2000;
  const timeout = input.timeoutMs ?? 120_000;
  const deadline = Date.now() + timeout;
  let lastStatus: string | null = null;

  while (true) {
    if (input.signal?.aborted) throw new Error("aborted");
    const data = await getScoringJob(input);
    const status = String((data as { status?: unknown }).status ?? "unknown");
    if (status !== lastStatus) {
      lastStatus = status;
      logger.info("scoring job poll status", {
        scenario_id: input.scenarioId,
        transcript_object_id: input.transcriptObjectId,
        status,
        job_id: (data as { id?: string }).id ?? null,
      });
    }
    if (data.status === "ready") {
      const result = data.result;
      const gradeNum = typeof result?.grade === "number" ? result.grade : undefined;
      return {
        pass: result?.score === "pass",
        grade: gradeNum,
        breakdown: normalizeBreakdown(result?.score_breakdown),
      };
    }
    if (data.status === "error" || status === "failed") {
      throw new Error((data as { error_message?: string }).error_message || "Scoring job failed");
    }
    if (Date.now() + interval > deadline) {
      logger.error("scoring job poll timeout", {
        scenario_id: input.scenarioId,
        transcript_object_id: input.transcriptObjectId,
        last_status: status,
        timeout_ms: timeout,
      });
      throw new Error("Score timed out");
    }
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(resolve, interval);
      input.signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(t);
          reject(new Error("aborted"));
        },
        { once: true },
      );
    });
  }
}
